/* ============================================================
   Conversion 2026 — Behaviors
   - Image fade-in on load
   - Cart count bounce when items change
   - "Just added" toast on add-to-cart
   - Sticky ATC visibility on scroll
   - Section reveal on scroll (lightweight fallback)
   ============================================================ */
(function () {
  'use strict';

  if (window.__c2026Loaded) return;
  window.__c2026Loaded = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. Image fade-in ---- */
  function setupImageFadeIn() {
    var imgs = document.querySelectorAll('img');
    imgs.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () {
          img.classList.add('is-loaded');
        }, { once: true });
        img.addEventListener('error', function () {
          img.classList.add('is-loaded');
        }, { once: true });
      }
    });

    // Also catch images added dynamically
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            var newImgs = node.tagName === 'IMG' ? [node] : node.querySelectorAll ? node.querySelectorAll('img') : [];
            newImgs.forEach(function (img) {
              if (img.complete && img.naturalWidth > 0) {
                img.classList.add('is-loaded');
              } else {
                img.addEventListener('load', function () {
                  img.classList.add('is-loaded');
                }, { once: true });
              }
            });
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ---- 2. Cart count bounce ---- */
  function bounceCartCount() {
    var els = document.querySelectorAll('.cart-count-bubble, .gb-cart-total-item, [data-cart-count]');
    els.forEach(function (el) {
      el.classList.remove('c2026-bounce');
      // force reflow so the animation restarts
      void el.offsetWidth;
      el.classList.add('c2026-bounce');
      setTimeout(function () {
        el.classList.remove('c2026-bounce');
      }, 700);
    });
  }

  /* ---- 3. "Just added" toast ---- */
  var toastEl = null;
  var toastTimer = null;

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'c2026-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<span class="c2026-toast__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
          '<polyline points="5 12 10 17 19 7"/>' +
        '</svg>' +
      '</span>' +
      '<span class="c2026-toast__text">Added to cart</span>';
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function showToast(text) {
    if (REDUCED) return;
    var el = ensureToast();
    if (text) {
      var t = el.querySelector('.c2026-toast__text');
      if (t) t.textContent = text;
    }
    clearTimeout(toastTimer);
    // restart entrance animation
    el.classList.remove('is-visible');
    void el.offsetWidth;
    el.classList.add('is-visible');
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 2400);
  }

  /* ---- 4. Hook into Shopify cart events ---- */
  function setupCartHooks() {
    // PubSub from theme (Shopify Dawn-based themes)
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
          bounceCartCount();
          showToast('Added to cart');
        });
      } catch (e) { /* ignore */ }
    }

    // Intercept fetch responses for /cart/add (works regardless of theme JS)
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var args = arguments;
        var url = args[0];
        var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
        var isCartAdd = urlStr.indexOf('/cart/add') !== -1;
        return origFetch.apply(this, args).then(function (response) {
          if (isCartAdd && response.ok) {
            setTimeout(function () {
              bounceCartCount();
              showToast('Added to cart');
            }, 50);
          }
          return response;
        });
      };
    }

    // Listen to manual add-to-cart form submits as a fallback
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') !== -1) {
        // We'll let fetch interception handle the success case if AJAX,
        // and for full-page submits the next page will show the cart anyway.
      }
    }, true);
  }

  /* ---- 5. Sticky ATC — Disabled. Theme handles its own visibility. ---- */
  function setupStickyATC() {
    /* intentionally no-op */
  }

  /* ---- 6. Section reveal on scroll — applied to EVERY Shopify section ---- */
  function setupReveal() {
    if (REDUCED) return;
    if (!('IntersectionObserver' in window)) return;

    // Sections we want to skip (critical layout, drawers, sticky bars).
    // Shopify uses BOTH hyphens (section types) AND underscores (block IDs) — match both.
    // scrolling-features-bar removed: it's a normal content section, should animate.
    var SKIP_PATTERNS = [
      'header',
      'footer',
      'cart-drawer', 'cart_drawer',
      'cart-notification', 'cart_notification',
      'cart-live-region', 'cart_live_region',
      'sticky-add-to-cart', 'sticky_add_to_cart',
      'announcement-bar', 'announcement_bar'
    ];

    function shouldSkip(el) {
      // Normalize: lowercase + replace underscores with hyphens to match either format
      var id = (el.id || '').toLowerCase().replace(/_/g, '-');
      // Also skip if element is position:fixed/sticky (those don't belong in scroll-reveal flow)
      var pos = window.getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') return true;
      for (var i = 0; i < SKIP_PATTERNS.length; i++) {
        var pattern = SKIP_PATTERNS[i].toLowerCase().replace(/_/g, '-');
        if (id.indexOf(pattern) !== -1) return true;
      }
      return false;
    }

    // Target every Shopify-rendered section
    var sections = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sections.length) {
      // Fallback for non-section-wrapped layouts
      sections = document.querySelectorAll('main > section, main > div');
    }

    // Mark every non-skipped section with .c2026-reveal.
    // The IntersectionObserver below handles entering/leaving viewport.
    // Sections already visible on load will animate in dramatically — desired effect.
    sections.forEach(function (sec) {
      if (shouldSkip(sec)) return;
      if (!sec.classList.contains('c2026-reveal')) {
        sec.className += ' c2026-reveal';
      }
    });

    // Bidirectional observer: toggle is-visible on enter AND exit.
    // When user scrolls back UP, the section animates IN again.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        } else {
          // Only remove the class if the section is BELOW the viewport (scrolled past it from above)
          // or ABOVE the viewport (scrolled past it going down) — basically always when not visible.
          // This way re-entering from any direction triggers the animation again.
          entry.target.classList.remove('is-visible');
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -80px 0px'
    });

    // Observe ALL marked sections (don't unobserve — keep watching for re-entry)
    document.querySelectorAll('.c2026-reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---- Init ---- */
  function init() {
    setupImageFadeIn();
    setupCartHooks();
    setupStickyATC();
    setupReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
